import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  CheckCircle2, MapPin, Phone, Mail, Globe, MessageCircle, Clock,
  Users, Home as HomeIcon, Instagram, Facebook, Linkedin,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import PropertyCard from '../components/PropertyCard';
import SEO from '../components/SEO';
import VideoEmbedCard from '../components/profile/VideoEmbedCard';
import GalleryGrid from '../components/profile/GalleryGrid';
import { HistoryCard, StatsSoonCard } from '../components/profile/ProfileCards';
import { api, friendlyError } from '../lib/api';
import { track, EventTypes } from '../lib/analytics';

const TABS = [
  { id: 'about', label: 'Acerca de' },
  { id: 'properties', label: 'Propiedades' },
  { id: 'team', label: 'Equipo' },
];

export default function AgencyProfile() {
  const { slug } = useParams();
  const [agency, setAgency] = useState(null);
  const [tab, setTab] = useState('about');
  const [props, setProps] = useState([]);
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.getAgency(slug),
      api.getAgencyProperties(slug, { limit: 24 }),
      api.getAgencyTeam(slug),
    ])
      .then(([a, p, t]) => {
        setAgency(a); setProps(p.items); setTeam(t.items);
        if (a?.id) track(EventTypes.AGENCY_PROFILE_VIEWED, { agency_id: a.id });
      })
      .catch((e) => setErr(friendlyError(e, 'No pudimos cargar la inmobiliaria.')))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return (<div className="min-h-screen flex items-center justify-center"><div className="w-10 h-10 border-4 border-habita border-t-transparent rounded-full animate-spin" /></div>);
  if (err || !agency) return (<><Header /><div className="max-w-xl mx-auto p-12 text-center"><p>{err || 'No encontrado'}</p></div><Footer /></>);

  const whatsappLink = agency.whatsapp ? `https://wa.me/${agency.whatsapp.replace(/\D/g, '')}` : null;
  const cover = agency.cover_url || 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=1600&h=500&fit=crop';

  return (
    <div className="min-h-screen bg-habita-cream">
      <SEO
        title={`${agency.name} · Inmobiliaria en ${agency.city || 'Colombia'}`}
        description={agency.description || `Conoce a ${agency.name}, inmobiliaria en ${agency.city || 'Colombia'}. ${agency.active_properties} propiedades activas y un equipo de ${agency.active_members} asesores.`}
        image={agency.logo_url || cover}
        jsonLd={{
          '@context': 'https://schema.org', '@type': 'RealEstateAgent',
          name: agency.name, image: agency.logo_url,
          address: { '@type': 'PostalAddress', addressLocality: agency.city, addressCountry: 'CO' },
          telephone: agency.phone, email: agency.email, url: agency.website,
        }}
      />
      <Header />

      {/* HERO */}
      <div className="relative">
        <div className="h-48 md:h-64 lg:h-80 overflow-hidden bg-habita-dark">
          <img src={cover} alt="" className="w-full h-full object-cover opacity-90" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
        </div>
        <div className="max-w-6xl mx-auto px-4 lg:px-8 -mt-16 lg:-mt-20 relative">
          <div className="flex flex-col md:flex-row md:items-end gap-5">
            <div className="w-28 h-28 lg:w-36 lg:h-36 rounded-2xl bg-white border-4 border-white shadow-xl overflow-hidden shrink-0">
              {agency.logo_url
                ? <img src={agency.logo_url} alt={agency.name} className="w-full h-full object-cover" />
                : <div className="w-full h-full bg-habita-mint flex items-center justify-center text-habita text-3xl font-bold">{agency.name.charAt(0)}</div>}
            </div>
            <div className="flex-1 min-w-0 pb-2">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="font-serif text-2xl lg:text-4xl font-bold text-habita-dark break-words">{agency.name}</h1>
                {agency.verified && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold" data-testid="agency-verified-badge">
                    <CheckCircle2 className="w-3.5 h-3.5" /> VERIFICADA
                  </span>
                )}
              </div>
              <div className="text-sm text-gray-600 mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
                {agency.city && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {agency.city}</span>}
                <span className="flex items-center gap-1"><HomeIcon className="w-3.5 h-3.5" /> {agency.active_properties} propiedades</span>
                <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {agency.active_members} asesores</span>
                {agency.founded_year && <span>· Desde {agency.founded_year}</span>}
              </div>
            </div>
            <div className="flex flex-wrap gap-2 pb-2 shrink-0">
              {whatsappLink && (
                <a
                  href={whatsappLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="agency-whatsapp-btn"
                  onClick={() => track(EventTypes.AGENCY_WHATSAPP_CLICKED, { agency_id: agency.id })}
                  className="btn-habita inline-flex items-center gap-1.5 px-4 h-10 rounded-full text-sm font-semibold"
                >
                  <MessageCircle className="w-4 h-4" /> WhatsApp
                </a>
              )}
              {agency.website && (
                <a
                  href={agency.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => track(EventTypes.AGENCY_WEBSITE_CLICKED, { agency_id: agency.id })}
                  className="inline-flex items-center gap-1.5 px-4 h-10 rounded-full text-sm font-semibold border border-gray-300 hover:border-habita bg-white"
                  data-testid="agency-website-btn"
                >
                  <Globe className="w-4 h-4" /> Web
                </a>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* TABS */}
      <div className="max-w-6xl mx-auto px-4 lg:px-8 mt-8 sticky top-16 bg-habita-cream z-20 border-b border-gray-200">
        <div className="flex gap-1">
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`agency-tab-${t.id}`}
              className={`px-4 py-3 text-sm font-semibold border-b-2 transition-colors ${tab === t.id ? 'border-habita text-habita' : 'border-transparent text-gray-600 hover:text-habita-dark'}`}>
              {t.label}{t.id === 'properties' ? ` (${agency.active_properties})` : t.id === 'team' ? ` (${agency.active_members})` : ''}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 lg:px-8 py-8 grid lg:grid-cols-3 gap-8">
        <main className="lg:col-span-2 space-y-6">
          {tab === 'about' && (
            <>
              <div className="bg-white rounded-2xl border border-gray-200 p-6" data-testid="agency-about-card">
                <h2 className="font-serif text-xl font-bold text-habita-dark">Sobre nosotros</h2>
                <p className="text-sm text-gray-700 mt-3 whitespace-pre-line leading-relaxed">
                  {agency.description || `${agency.name} es una inmobiliaria activa en ${agency.city || 'Colombia'}.`}
                </p>
                {agency.specialties?.length > 0 && (
                  <div className="mt-5">
                    <p className="text-xs uppercase tracking-widest text-gray-500 mb-2">Especialidades</p>
                    <div className="flex flex-wrap gap-2" data-testid="agency-specialties">
                      {agency.specialties.map(s => (<span key={s} className="px-3 py-1.5 rounded-full bg-habita-mint text-habita text-xs font-semibold">{s}</span>))}
                    </div>
                  </div>
                )}
                {agency.cities_operating?.length > 0 && (
                  <div className="mt-5">
                    <p className="text-xs uppercase tracking-widest text-gray-500 mb-2">Ciudades donde operamos</p>
                    <div className="flex flex-wrap gap-2" data-testid="agency-cities-operating">
                      {agency.cities_operating.map(c => (
                        <span key={c} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-habita-cream border border-habita/20 text-habita-dark text-xs font-semibold">
                          <MapPin className="w-3 h-3 text-habita" /> {c}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <HistoryCard text={agency.history} title="Nuestra historia" testid="agency-history-card" />
              <VideoEmbedCard url={agency.video_url} title={`${agency.name} · Video institucional`} testid="agency-video-card" />
              <GalleryGrid images={agency.gallery} title="Galería" testid="agency-gallery" altPrefix={`${agency.name} galería`} />
              <StatsSoonCard
                labels={['Vistas', 'Contactos WhatsApp', 'Leads recibidos', 'Conversión']}
                title="Estadísticas"
                testid="agency-stats-soon"
              />
            </>
          )}

          {tab === 'properties' && (
            props.length === 0
              ? <div className="bg-white rounded-2xl p-12 text-center text-gray-500">Aún no hay propiedades publicadas.</div>
              : <div className="grid grid-cols-1 md:grid-cols-2 gap-5">{props.map(p => <PropertyCard key={p.id} p={p} />)}</div>
          )}

          {tab === 'team' && (
            team.length === 0
              ? <div className="bg-white rounded-2xl p-12 text-center text-gray-500">Aún no hay asesores activos.</div>
              : <div className="grid sm:grid-cols-2 gap-4">
                  {team.map(t => {
                    const clickable = !!t.slug;
                    const inner = (
                      <div className="flex items-center gap-3">
                        <div className="relative shrink-0">
                          <img src={t.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(t.name)}&background=00824D&color=fff`} alt={t.name} className="w-14 h-14 rounded-full object-cover" />
                          {t.verified && (
                            <span className="absolute -bottom-0.5 -right-0.5 w-5 h-5 rounded-full bg-habita text-white flex items-center justify-center ring-2 ring-white" title="Agente verificado">
                              <CheckCircle2 className="w-3 h-3" />
                            </span>
                          )}
                        </div>
                        <div className="min-w-0">
                          <p className="font-semibold text-habita-dark truncate group-hover:text-habita transition-colors">{t.name}</p>
                          <p className="text-xs text-gray-500 truncate">{t.title}</p>
                          <p className="text-xs text-habita mt-0.5">{t.active_properties} propiedades</p>
                        </div>
                        {clickable && (
                          <span className="ml-auto text-xs text-habita opacity-0 group-hover:opacity-100 transition-opacity font-semibold shrink-0">Ver perfil →</span>
                        )}
                      </div>
                    );
                    return clickable ? (
                      <Link
                        key={t.id}
                        to={`/agente/${t.slug}`}
                        onClick={() => track('agency.team_member_clicked', { agency_id: agency.id, agent_id: t.id })}
                        className="group bg-white rounded-2xl border border-gray-200 p-5 hover:shadow-md hover:border-habita transition-all"
                        data-testid={`agency-team-member-${t.id}`}
                      >
                        {inner}
                      </Link>
                    ) : (
                      <div key={t.id} className="group bg-white rounded-2xl border border-gray-200 p-5" data-testid={`agency-team-member-${t.id}`}>
                        {inner}
                      </div>
                    );
                  })}
                </div>
          )}
        </main>

        <aside className="space-y-4">
          <div className="bg-white rounded-2xl border border-gray-200 p-5">
            <h3 className="font-serif text-base font-bold text-habita-dark mb-3">Contacto</h3>
            <div className="space-y-2.5 text-sm">
              {agency.phone && <div className="flex items-center gap-2 text-gray-700"><Phone className="w-4 h-4 text-habita" /> <a href={`tel:${agency.phone}`}>{agency.phone}</a></div>}
              {agency.email && <div className="flex items-center gap-2 text-gray-700"><Mail className="w-4 h-4 text-habita" /> <a href={`mailto:${agency.email}`}>{agency.email}</a></div>}
              {agency.website && <div className="flex items-center gap-2 text-gray-700"><Globe className="w-4 h-4 text-habita" /> <a href={agency.website} target="_blank" rel="noopener noreferrer">{agency.website.replace(/^https?:\/\//, '')}</a></div>}
              {agency.hours && Object.keys(agency.hours).length > 0 && (
                <div className="pt-2 border-t border-gray-100 text-xs text-gray-600">
                  <p className="flex items-center gap-1 font-semibold mb-1"><Clock className="w-3.5 h-3.5" /> Horarios</p>
                  {Object.entries(agency.hours).map(([day, h]) => (
                    <div key={day} className="flex justify-between"><span className="capitalize">{day}</span><span>{h?.open || '—'} - {h?.close || '—'}</span></div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {agency.social && Object.keys(agency.social).filter(k => agency.social[k]).length > 0 && (
            <div className="bg-white rounded-2xl border border-gray-200 p-5">
              <h3 className="font-serif text-base font-bold text-habita-dark mb-3">Síguenos</h3>
              <div className="flex gap-2">
                {agency.social.instagram && <a href={agency.social.instagram} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-habita-mint flex items-center justify-center text-habita hover:bg-habita hover:text-white transition-colors"><Instagram className="w-4 h-4" /></a>}
                {agency.social.facebook && <a href={agency.social.facebook} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-habita-mint flex items-center justify-center text-habita hover:bg-habita hover:text-white transition-colors"><Facebook className="w-4 h-4" /></a>}
                {agency.social.linkedin && <a href={agency.social.linkedin} target="_blank" rel="noopener noreferrer" className="w-9 h-9 rounded-full bg-habita-mint flex items-center justify-center text-habita hover:bg-habita hover:text-white transition-colors"><Linkedin className="w-4 h-4" /></a>}
              </div>
            </div>
          )}
        </aside>
      </div>

      <Footer />
    </div>
  );
}
